// src/Pages/Dashboard/widgets/ProjectView/Tabs/Reports/MaterialReportPreview.js

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosWithAuth from "../../../../../../Redux/UtilsRedux/axiosWithAuth";
import html2pdf from "html2pdf.js";
import { API_ROUTES } from "../../../../../../Redux/Constants/apiRoutes";
import "../../../../../../Stylesheets/Dashboard/Tabs/Reports/ExpenseReportPrint.css";
import site360Logo from "../../../../../../assets/images/logo.png";
import { FaChevronLeft } from "react-icons/fa";

import MaterialReportModal from "../Material/Component/MaterialReportModal";

const formatCurrency = (val) => {
  if (val == null || Number.isNaN(Number(val))) return "₹0";
  return "₹" + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDateTime = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MaterialReportPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const queryParams = useMemo(
    () => ({
      type: qs.get("type") || "materials",
      projectId: qs.get("projectId"),
      from: qs.get("from"),
      to: qs.get("to"),
      detail: qs.get("detail") || "summary",
      category: qs.get("category") || "all",
    }),
    [qs]
  );

  const isDetailed = queryParams.detail === "detailed";

  // ---------- fetch JSON ----------
  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!queryParams.projectId || !queryParams.from || !queryParams.to) {
          setError("Missing projectId / from / to");
          setLoading(false);
          return;
        }

        const params = new URLSearchParams({
          type: "materials",
          projectId: queryParams.projectId,
          from: queryParams.from,
          to: queryParams.to,
          detail: queryParams.detail,
          category: queryParams.category,
          format: "json",
        }).toString();

        const url = `${API_ROUTES.GENERATE_REPORT}?${params}`;
        const res = await axiosWithAuth.get(url);

        setReport(res.data.data);
        setError(null);
      } catch (err) {
        console.error("Material report fetch failed", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load material report data";
        setError(msg);
        setReport(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [queryParams]);

  // ---------- PDF download ----------
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPdf = async () => {
    if (!queryParams.projectId || !queryParams.from || !queryParams.to) return;

    const container = document.getElementById("material-report-doc");
    if (!container) {
      console.error("Material report container not found");
      return;
    }

    try {
      setDownloading(true);

      const filename = `material-report-${queryParams.from}-to-${queryParams.to}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      };

      await html2pdf().set(opt).from(container).save();
    } catch (e) {
      console.error("Material PDF download failed", e);
    } finally {
      setDownloading(false);
    }
  };

  // ---------- navigation ----------
  const handleBackToMaterials = () => {
    if (location.state?.returnTo) {
      navigate(location.state.returnTo);
      return;
    }
    navigate(-1);
  };

  // ---------- filters modal state ----------
  const [showFilters, setShowFilters] = useState(false);
  const [tmpFrom, setTmpFrom] = useState(queryParams.from || "");
  const [tmpTo, setTmpTo] = useState(queryParams.to || "");
  const [tmpCategory, setTmpCategory] = useState(queryParams.category || "all");
  const [tmpDetail, setTmpDetail] = useState(queryParams.detail || "summary");

  useEffect(() => {
    if (!showFilters) return;
    setTmpFrom(queryParams.from || "");
    setTmpTo(queryParams.to || "");
    setTmpCategory(queryParams.category || "all");
    setTmpDetail(queryParams.detail || "summary");
  }, [showFilters, queryParams]);

  const handleApplyFilters = () => {
    const next = new URLSearchParams(location.search);

    next.set("from", tmpFrom);
    next.set("to", tmpTo);
    next.set("detail", tmpDetail);
    next.set("category", tmpCategory);
    next.set("type", "materials");

    setLoading(true);
    setError(null);

    navigate({ search: next.toString() }, { replace: false });

    setShowFilters(false);
  };

  // ---------- loading / error ----------
  if (loading) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-loading">Preparing material report…</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="report-print-root">
        <div className="report-preview-scroll">
          <div className="report-print-shell">
            <div className="report-error">
              <h2>Unable to generate material report</h2>
              <p>{error || "No data available for the selected filters."}</p>
              <button
                className="report-filter-btn"
                onClick={() => setShowFilters(true)}
              >
                Edit Filters
              </button>
              <button
                className="report-back-btn"
                style={{ marginTop: 8 }}
                onClick={handleBackToMaterials}
              >
                <FaChevronLeft size={14} />
              </button>
            </div>
          </div>
        </div>

        <MaterialReportModal
          show={showFilters}
          onClose={() => setShowFilters(false)}
          startDate={tmpFrom}
          endDate={tmpTo}
          setStartDate={setTmpFrom}
          setEndDate={setTmpTo}
          category={tmpCategory}
          setCategory={setTmpCategory}
          format={tmpDetail}
          setFormat={setTmpDetail}
          mode="filters"
          onApply={handleApplyFilters}
          applyLabel="Apply Filters"
        />
      </div>
    );
  }

  const { meta = {}, summary = {}, tables = [] } = report;

  // main per-material summary table
  const materialSummaryTable =
    tables.find((t) => /material summary/i.test(t.title || "")) ||
    tables[0] ||
    null;

  // daily movement (if you ever want to use it separately later)
  const dailyMovementTable =
    tables.find((t) => /daily material movement/i.test(t.title || "")) ||
    null;

  const topTable =
    tables.find((t) => /top/i.test(t.title || "")) ||
    (tables.length > 1 ? tables[1] : null);

  return (
    <div className="report-print-root">
      {/* top toolbar */}
      <div className="report-toolbar">
        <div className="report-toolbar-left">
          <button
            className="report-back-btn"
            onClick={handleBackToMaterials}
            aria-label="Back to Materials"
          >
            <FaChevronLeft size={14} />
          </button>

          <div className="report-toolbar-title">
            <h2>Material Report Preview</h2>
            <span className="report-toolbar-period">
              {queryParams.from} → {queryParams.to}
            </span>
          </div>
        </div>

        <div className="report-toolbar-right">
          <button
            className="report-filter-btn"
            onClick={() => setShowFilters(true)}
          >
            Edit Filters
          </button>

          <button
            className="report-download-btn"
            onClick={handleDownloadPdf}
            disabled={downloading}
          >
            {downloading ? "Generating PDF…" : "Download PDF"}
          </button>
        </div>
      </div>

      <div className="report-watermark">Site360</div>

      <div className="report-preview-scroll">
        <div id="material-report-doc" className="report-print-shell">
          <img
            src={site360Logo}
            alt=""
            className="report-doc-watermark"
            crossOrigin="anonymous"
          />

          {/* HEADER */}
          <header className="report-header">
            <div className="report-header-left">
              <div className="report-logo">Site360</div>
              <div className="report-title-block">
                <h1>Material Report</h1>
                {meta.projectName && (
                  <p className="report-project">{meta.projectName}</p>
                )}
              </div>
            </div>

            <div className="report-header-right">
              <div className="report-meta-row">
                <span className="label">Type</span>
                <span className="value">Materials</span>
              </div>

              <div className="report-meta-row">
                <span className="label">Period</span>
                <span className="value">
                  {(meta.from || queryParams.from || "—")} →{" "}
                  {(meta.to || queryParams.to || "—")}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Detail</span>
                <span className="value">
                  {isDetailed ? "Detailed" : "Summary"}
                </span>
              </div>

              <div className="report-meta-row">
                <span className="label">Generated</span>
                <span className="value">{formatDateTime(meta.generatedAt)}</span>
              </div>
            </div>
          </header>

          {/* SUMMARY */}
          <section className="report-section">
            <div className="section-header">
              <h2>Summary</h2>
              <p>High-level snapshot of material movement and spend.</p>
            </div>

            <div className="metric-grid">
              <div className="metric-card">
                <div className="metric-label">Total Spend</div>
                <div className="metric-value">
                  {formatCurrency(summary.totalSpend)}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Materials with Activity</div>
                <div className="metric-value">
                  {summary.totalMaterials ?? 0}
                </div>
                <div className="metric-helper">
                  Receives: {summary.totalReceiveEntries ?? 0} · Usage:{" "}
                  {summary.totalUsageEntries ?? 0}
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-label">Pending Bills</div>
                <div className="metric-value">
                  {summary.pendingBills ?? summary.pendingBill ?? 0}
                </div>
              </div>
            </div>
          </section>

          {/* MAIN MATERIAL SUMMARY TABLE */}
          {materialSummaryTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{materialSummaryTable.title || "Material Summary"}</h3>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {materialSummaryTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {materialSummaryTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={i === row.length - 1 ? "right" : ""}
                        >
                          {cell ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* (Optional) DAILY MOVEMENT – if you decide to show it later */}
          {dailyMovementTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>
                  {dailyMovementTable.title || "Daily Material Movement"}
                </h3>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {dailyMovementTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyMovementTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={i > 0 ? "right" : ""}
                        >
                          {i === 0 ? formatDate(cell) : cell ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* TOP MATERIALS TABLE – only for DETAILED */}
          {isDetailed && topTable && (
            <section className="report-section">
              <div className="section-header">
                <h3>{topTable.title || "Top Materials by Spend"}</h3>
                <p>Materials contributing the most to project spend.</p>
              </div>

              <table className="report-table">
                <thead>
                  <tr>
                    {topTable.columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topTable.rows.map((row, idx) => (
                    <tr key={idx}>
                      {row.map((cell, i) => (
                        <td
                          key={i}
                          className={
                            i === row.length - 1 || i === 2 ? "right" : ""
                          }
                        >
                          {cell ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* FOOTER */}
          <footer className="report-footer">
            <div className="footer-brand">Site360</div>
            <div className="footer-text">
              Construction Management Platform · https://site360.app
            </div>
          </footer>
        </div>
      </div>

      {/* bottom sticky like Expense */}
      <div className="report-bottom-sticky">
        <div className="bottom-meta">
          <div className="title">Material Report</div>
          <div className="period">
            {queryParams.from} → {queryParams.to}
          </div>
        </div>

        <button
          className="report-download-btn"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? "Generating PDF…" : "Download PDF"}
        </button>
      </div>

      {/* Filters modal */}
      <MaterialReportModal
        show={showFilters}
        onClose={() => setShowFilters(false)}
        startDate={tmpFrom}
        endDate={tmpTo}
        setStartDate={setTmpFrom}
        setEndDate={setTmpTo}
        category={tmpCategory}
        setCategory={setTmpCategory}
        format={tmpDetail}
        setFormat={setTmpDetail}
        mode="filters"
        onApply={handleApplyFilters}
        applyLabel="Apply Filters"
      />
    </div>
  );
};

export default MaterialReportPreview;
